/**
 * /food — screen 3f, plus the add flow (3g duplicate interruption, 3h details).
 *
 * The screen is a list, but the product is the *interruption* (D53): tapping
 * "I'm ordering something" asks the server whether anyone is already on that
 * category before it will show a form. If someone is, the sheet stops being a
 * form and becomes a three-way choice, and the primary of the three is to join
 * rather than to duplicate.
 *
 * Joining an order is an add-on (D117), never an edit — the owner is the person
 * who will call the restaurant, and add-ons deliberately do not move the
 * sufficiency estimate (D54).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FOOD_CATEGORIES,
  type FoodCategory, type FoodConflict, type FoodIntent, type FoodOrder,
  type FoodSummary, type VendorSuggestion,
} from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, DEL, GET, POST, writeOrQueue } from '../lib/api'
import {
  Body, Button, Card, EmptyState, Eyebrow, Field, Header, Notice,
  Screen, Sheet, SkeletonCard, Spacer,
} from '../ui'

// ── formatting ──────────────────────────────────────────────────────────────

const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

/** "12:55" — ETA and arrival times drop the meridiem, as the prototype does. */
function bareTime(at: number): string {
  return HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')
}

const EMOJI = new Map(FOOD_CATEGORIES.map((c) => [c.key, c.emoji]))
const LABEL = new Map(FOOD_CATEGORIES.map((c) => [c.key, c.label]))

function emojiFor(c: FoodCategory): string { return EMOJI.get(c) ?? '🍽️' }
function labelFor(c: FoodCategory): string { return LABEL.get(c) ?? 'Food' }
function lowerLabel(c: FoodCategory): string { return labelFor(c).toLowerCase() }

/** The `bright` mono countdown on the right of an in-flight card. */
function etaCountdown(etaAt: number | null, now: number): string {
  if (etaAt === null) return 'no ETA'
  const mins = Math.round((etaAt - now) / 60_000)
  if (mins > 90) return bareTime(etaAt)
  if (mins > 0) return `${mins} min`
  if (mins === 0) return 'any minute'
  return `${-mins} min late`
}

function minutesAgo(at: number, now: number): string {
  const mins = Math.max(0, Math.round((now - at) / 60_000))
  if (mins < 1) return 'just now'
  return `${mins} min ago`
}

function expiresIn(at: number, now: number): string {
  const mins = Math.max(0, Math.round((at - now) / 60_000))
  return `${mins} min`
}

/** A ticking clock for countdowns; 15s is plenty for minute-resolution copy. */
function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

/** "6 large, 2 veggie" → 6. The estimate is better with a number than without. */
function quantityFrom(contents: string): number | undefined {
  const m = /^\s*(\d{1,3})\b/.exec(contents)
  if (!m) return undefined
  const n = parseInt(m[1], 10)
  return n >= 1 && n <= 999 ? n : undefined
}

// ── wire shapes ─────────────────────────────────────────────────────────────

type FoodResponse = FoodSummary & { vendorSuggestions?: VendorSuggestion[] }

const ETA_CHOICES = [20, 30, 45, 60] as const

type Step = 'category' | 'conflict' | 'join' | 'mode' | 'details'

// ── screen ──────────────────────────────────────────────────────────────────

export default function Food() {
  const { state, toast, refresh } = useStore()
  const navigate = useNavigate()
  const now = useNow()

  const gameDay = state?.gameDay.date ?? null
  const meId = state?.user.id ?? ''
  const isAdmin = state?.user.isAdmin ?? false

  const [summary, setSummary] = useState<FoodResponse | null>(state?.food ?? null)
  const [vendors, setVendors] = useState<VendorSuggestion[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(state?.food != null)
  const [busyId, setBusyId] = useState<string | null>(null)

  // sheets
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<FoodOrder | null>(null)
  const [joining, setJoining] = useState<{ order: FoodOrder | null; intent: FoodIntent | null } | null>(null)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const q = gameDay ? `?day=${encodeURIComponent(gameDay)}` : ''
      const next = await GET<FoodResponse>(`/api/food${q}`, signal)
      setSummary(next)
      if (next.vendorSuggestions?.length) setVendors(next.vendorSuggestions)
      setErr(null)
    } catch (e) {
      if (signal?.aborted) return
      // Last-known-good stays on screen; the notice explains itself (D103).
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh the food list.')
    } finally {
      if (!signal?.aborted) setLoaded(true)
    }
  }, [gameDay])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  // Vendor history backs the combobox in step 3 (D55).
  useEffect(() => {
    const ac = new AbortController()
    GET<{ vendors: VendorSuggestion[] }>('/api/food/vendors', ac.signal)
      .then((r) => setVendors(r.vendors))
      .catch(() => { /* the combobox degrades to a plain text field */ })
    return () => ac.abort()
  }, [])

  const orders = summary?.orders ?? []
  const inFlight = useMemo(
    () => orders.filter((o) => !o.deliveredAt && !o.cancelledAt),
    [orders],
  )
  const delivered = useMemo(
    () => orders.filter((o) => o.deliveredAt && !o.cancelledAt)
      .sort((a, b) => (a.deliveredAt ?? 0) - (b.deliveredAt ?? 0)),
    [orders],
  )
  const intents = useMemo(
    () => (summary?.intents ?? []).filter((i) => i.expiresAt > now),
    [summary, now],
  )

  const headcount = summary?.headcount ?? state?.attendance?.headcount ?? 0
  const headline = `${headcount} ${headcount === 1 ? 'person' : 'people'} · ${inFlight.length} ${inFlight.length === 1 ? 'order' : 'orders'} in flight`
  const sufficiency = summary?.sufficiencyLabel ?? ''

  // ── writes ────────────────────────────────────────────────────────────────

  const markDelivered = useCallback(async (order: FoodOrder) => {
    setBusyId(order.id)
    try {
      const res = await writeOrQueue<{ order: FoodOrder }>(
        `/api/food/orders/${order.id}/delivered`, 'POST', {}, `${order.vendor ?? order.contents} arrived`,
      )
      if (res === null) toast('Saved. It will send when you’re back online')
      else await load()
      void refresh(true)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t mark that as arrived.')
    } finally {
      setBusyId(null)
    }
  }, [load, refresh, toast])

  const afterWrite = useCallback(async () => {
    await load()
    void refresh(true)
  }, [load, refresh])

  const empty = loaded && !inFlight.length && !intents.length && !delivered.length

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        // A counted sentence, not a name, and the only screen title in the app
        // that grows with the data — "12 people · 10 orders in flight" at the
        // usual 20px ran straight under the badge on one side and Past days on
        // the other. At 13px it clears both with room to spare, and it reads
        // as the status line it actually is.
        title={<span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0 }}>{headline}</span>}
        sub={sufficiency ? <span style={{ fontSize: 13, color: 'var(--dim)' }}>{sufficiency}</span> : null}
        bordered={false}
        right={
          <button
            onClick={() => navigate('/food/archive')}
            style={{ fontSize: 12, color: 'var(--bright)', fontWeight: 700, minHeight: 44, flex: '0 0 auto' }}
          >
            Past days
          </button>
        }
      />

      <Body style={{ padding: '0 var(--gutter) 16px' }}>
        {!loaded ? (
          <>
            <Spacer h={4} />
            <SkeletonCard lines={4} />
            <Spacer h={12} />
            <SkeletonCard lines={3} />
          </>
        ) : (
          <>
            {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

            {empty ? (
              <EmptyState
                title="Nothing ordered yet."
                detail="Somebody has to go first, and everyone remembers who did."
                actionLabel="I’m ordering something"
                onAction={() => setAddOpen(true)}
              />
            ) : null}

            {inFlight.length ? (
              <>
                <Eyebrow>In flight</Eyebrow>
                {inFlight.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    now={now}
                    mine={o.userId === meId || isAdmin}
                    busy={busyId === o.id}
                    onDelivered={() => void markDelivered(o)}
                    onEdit={() => setEditing(o)}
                    onJoin={() => setJoining({ order: o, intent: null })}
                  />
                ))}
              </>
            ) : null}

            {intents.length ? (
              <>
                <Eyebrow style={{ marginTop: inFlight.length ? 22 : 0 }}>
                  Intentions · expire in 30 min
                </Eyebrow>
                {intents.map((i) => (
                  <IntentCard
                    key={i.id}
                    intent={i}
                    now={now}
                    mine={i.userId === meId}
                    onJoin={() => setJoining({ order: null, intent: i })}
                    onCancel={async () => {
                      setBusyId(i.id)
                      try {
                        await DEL(`/api/food/intents/${i.id}`)
                        await afterWrite()
                      } catch (e) {
                        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t cancel that.')
                      } finally {
                        setBusyId(null)
                      }
                    }}
                  />
                ))}
              </>
            ) : null}

            {delivered.length ? (
              <>
                <Eyebrow style={{ marginTop: inFlight.length || intents.length ? 22 : 0 }}>Delivered</Eyebrow>
                {delivered.map((o) => (
                  <div
                    key={o.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 12,
                      padding: '12px 0', borderBottom: '1px solid var(--line)', fontSize: 13,
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      {emojiFor(o.category)} {o.vendor ?? o.contents} · {o.displayName}
                    </span>
                    <span style={{ color: 'var(--dim)', flex: '0 0 auto' }}>
                      arrived {o.deliveredAt ? bareTime(o.deliveredAt) : '-'}
                    </span>
                  </div>
                ))}
              </>
            ) : null}
          </>
        )}
      </Body>

      {/* The sticky primary sits on a `bg` ground above the safe area. */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '12px var(--gutter) calc(22px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bg)',
          borderTop: '1px solid var(--line)',
        }}
      >
        <Button onClick={() => setAddOpen(true)}>+ I’m ordering something</Button>
      </div>

      <AddSheet
        open={addOpen}
        gameDay={gameDay}
        vendors={vendors}
        onClose={() => setAddOpen(false)}
        onDone={async (message) => {
          setAddOpen(false)
          if (message) toast(message)
          await afterWrite()
        }}
      />

      <EditSheet
        order={editing}
        onClose={() => setEditing(null)}
        onDone={async () => { setEditing(null); await afterWrite() }}
      />

      <JoinSheet
        target={joining}
        orders={orders}
        onClose={() => setJoining(null)}
        onDone={async () => { setJoining(null); await afterWrite() }}
      />
    </Screen>
  )
}

// ── cards ───────────────────────────────────────────────────────────────────

function OrderCard({
  order, now, mine, busy, onDelivered, onEdit, onJoin,
}: {
  order: FoodOrder
  now: number
  mine: boolean
  busy: boolean
  onDelivered: () => void
  onEdit: () => void
  onJoin: () => void
}) {
  const title = `${emojiFor(order.category)} ${order.vendor ?? labelFor(order.category)} · ${order.contents}`
  const metaBits = [
    order.displayName,
    order.etaAt ? `ETA ${bareTime(order.etaAt)}` : null,
    order.notes ? `“${order.notes}”` : null,
  ].filter(Boolean) as string[]

  return (
    <Card style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, minWidth: 0 }}>{title}</span>
        <span className="mono" style={{ fontSize: 12, color: 'var(--bright)', flex: '0 0 auto' }}>
          {etaCountdown(order.etaAt, now)}
        </span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>
        {metaBits.join(' · ')}
        {order.editedAt ? ' · edited' : ''}
      </div>

      {order.addons.length ? (
        <div
          style={{
            fontSize: 12, color: 'var(--dim)', marginTop: 10,
            paddingLeft: 10, borderLeft: '2px solid var(--line)', lineHeight: 1.6,
          }}
        >
          {order.addons.map((a) => (
            <div key={a.id}>+ {a.displayName}: {a.request}</div>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={onDelivered}
          disabled={busy}
          style={{
            flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
            background: 'var(--fill)', color: 'var(--ink)', fontSize: 13, fontWeight: 600,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? '…' : 'It’s here'}
        </button>
        <button
          onClick={mine ? onEdit : onJoin}
          style={{
            flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
            border: '1px solid var(--line)', color: 'var(--dim)', fontSize: 13, fontWeight: 600,
          }}
        >
          {mine ? 'Edit' : 'Add to it'}
        </button>
      </div>
    </Card>
  )
}

function IntentCard({
  intent, now, mine, onJoin, onCancel,
}: {
  intent: FoodIntent
  now: number
  mine: boolean
  onJoin: () => void
  onCancel: () => void
}) {
  const qty = intent.approxQty ? `~${intent.approxQty}` : null
  const meta = [qty, `posted ${minutesAgo(intent.createdAt, now)}`, `expires in ${expiresIn(intent.expiresAt, now)}`]
    .filter(Boolean).join(' · ')

  return (
    <Card dashed style={{ marginTop: 10 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>
        {emojiFor(intent.category)} {mine ? 'You are' : `${intent.displayName} is`} about to order {lowerLabel(intent.category)}
      </div>
      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>{meta}</div>
      <button
        onClick={mine ? onCancel : onJoin}
        style={{
          display: 'block', width: '100%', marginTop: 12, textAlign: 'center',
          padding: 10, borderRadius: 10, minHeight: 44,
          border: '1px solid var(--line)', fontSize: 13, fontWeight: 600, color: 'var(--bright)',
        }}
      >
        {mine ? 'Never mind' : 'Join this'}
      </button>
    </Card>
  )
}

// ── the add flow (3g + 3h) ──────────────────────────────────────────────────

function AddSheet({
  open, gameDay, vendors, onClose, onDone,
}: {
  open: boolean
  gameDay: string | null
  vendors: VendorSuggestion[]
  onClose: () => void
  onDone: (message?: string) => Promise<void>
}) {
  const [step, setStep] = useState<Step>('category')
  const [category, setCategory] = useState<FoodCategory | null>(null)
  const [conflict, setConflict] = useState<FoodConflict | null>(null)
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // step 2
  const [approxQty, setApproxQty] = useState('')
  // step 3
  const [vendor, setVendor] = useState('')
  const [vendorOpen, setVendorOpen] = useState(false)
  const [contents, setContents] = useState('')
  const [etaMinutes, setEtaMinutes] = useState<number>(30)
  const [notes, setNotes] = useState('')
  // join branch
  const [request, setRequest] = useState('')

  const reset = useCallback(() => {
    setStep('category'); setCategory(null); setConflict(null)
    setChecking(false); setBusy(false); setError(null)
    setApproxQty(''); setVendor(''); setVendorOpen(false)
    setContents(''); setEtaMinutes(30); setNotes(''); setRequest('')
  }, [])

  useEffect(() => { if (open) reset() }, [open, reset])

  const day = gameDay ?? undefined

  /** D53 — the interruption runs before the form ever opens. */
  const chooseCategory = useCallback(async (c: FoodCategory) => {
    setCategory(c)
    setChecking(true)
    setError(null)
    try {
      const res = await POST<{ conflicts: FoodConflict[] }>('/api/food/check', { category: c, day })
      const first = res.conflicts[0] ?? null
      if (first) { setConflict(first); setStep('conflict') } else { setStep('mode') }
    } catch (e) {
      // A failed check must never block ordering — fall through to the form.
      setError(e instanceof ApiFailure ? e.message : null)
      setStep('mode')
    } finally {
      setChecking(false)
    }
  }, [day])

  const postIntent = useCallback(async () => {
    if (!category) return
    setBusy(true)
    setError(null)
    try {
      const res = await writeOrQueue<{ intent: FoodIntent }>(
        '/api/food/intents', 'POST',
        { category, approxQty: approxQty.trim() || undefined, day },
        `About to order ${lowerLabel(category)}`,
      )
      await onDone(res === null ? 'Saved. It will post when you’re back online' : 'Posted. Nobody else will double up')
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t post that.')
    } finally {
      setBusy(false)
    }
  }, [approxQty, category, day, onDone])

  const postOrder = useCallback(async () => {
    if (!category) return
    const what = contents.trim()
    if (!what) { setError('Say what you ordered.'); return }
    setBusy(true)
    setError(null)
    try {
      const res = await writeOrQueue<{ order: FoodOrder }>(
        '/api/food/orders', 'POST',
        {
          category,
          vendor: vendor.trim() || undefined,
          contents: what,
          quantity: quantityFrom(what),
          notes: notes.trim() || undefined,
          etaMinutes,
          day,
        },
        `Ordered ${vendor.trim() || lowerLabel(category)}`,
      )
      await onDone(res === null ? 'Saved. It will post when you’re back online' : undefined)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t post that order.')
    } finally {
      setBusy(false)
    }
  }, [category, contents, day, etaMinutes, notes, onDone, vendor])

  const postJoin = useCallback(async () => {
    if (!conflict) return
    const text = request.trim()
    if (!text) { setError('Say what you want added.'); return }
    if (conflict.type !== 'order') {
      setError(`${conflict.displayName} hasn’t placed it yet. You can add to it the moment they do.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await writeOrQueue(
        `/api/food/orders/${conflict.id}/join`, 'POST', { request: text },
        `Added to ${conflict.displayName}’s order`,
      )
      await onDone(res === null ? 'Saved. It will send when you’re back online' : `${conflict.displayName} will see your request`)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t add that request.')
    } finally {
      setBusy(false)
    }
  }, [conflict, onDone, request])

  const stepLabel =
    step === 'category' || step === 'conflict' || step === 'join' ? 'Step 1 of 3'
      : step === 'mode' ? 'Step 2 of 3'
        : 'Step 3 of 3 · already placed it'

  const vendorMatches = useMemo(() => {
    const q = vendor.trim().toLowerCase()
    // With nothing typed, lead with places used for this category before.
    const pool = q || !category
      ? vendors
      : vendors.filter((v) => !v.typicalCategory || v.typicalCategory === category)
    const matched = q ? pool.filter((v) => v.vendor.toLowerCase().includes(q)) : pool
    return matched.slice(0, 6)
  }, [category, vendor, vendors])

  const vendorHint = useMemo(() => {
    const q = vendor.trim().toLowerCase()
    if (!q) return null
    const hit = vendors.find((v) => v.vendor.toLowerCase() === q)
    return hit?.typicalEtaMin ? `${hit.vendor} usually takes ~${hit.typicalEtaMin} min` : null
  }, [vendor, vendors])

  return (
    <Sheet open={open} onClose={onClose} labelledBy="sfg-food-add">
      <Eyebrow large>{stepLabel}</Eyebrow>

      {step === 'category' ? (
        <>
          <div id="sfg-food-add" style={{ fontSize: 20, fontWeight: 800, marginTop: 10, letterSpacing: '-0.01em' }}>
            What are you getting?
          </div>
          <div
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 18,
              opacity: checking ? 0.6 : 1,
            }}
          >
            {FOOD_CATEGORIES.map((c) => (
              <button
                key={c.key}
                disabled={checking}
                onClick={() => void chooseCategory(c.key)}
                style={{
                  padding: '13px 14px', borderRadius: 999, minHeight: 48,
                  border: `1px solid ${category === c.key ? 'var(--fill)' : 'var(--line)'}`,
                  background: category === c.key ? 'var(--fill)' : 'transparent',
                  color: category === c.key ? 'var(--ink)' : 'var(--fg)',
                  fontSize: 14, fontWeight: 600, textAlign: 'center',
                }}
              >
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
          {error ? <><Spacer h={14} /><Notice tone="warn">{error}</Notice></> : null}
        </>
      ) : null}

      {step === 'conflict' && conflict ? (
        <>
          <div id="sfg-food-add" style={{ fontSize: 20, fontWeight: 800, marginTop: 10, letterSpacing: '-0.01em' }}>
            ⚠️ {conflict.displayName} is already ordering {lowerLabel(conflict.category)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
            Posted {conflict.minutesAgo} {conflict.minutesAgo === 1 ? 'minute' : 'minutes'} ago
            {conflict.approxQty ? ` · ~${conflict.approxQty}` : ''}
            {conflict.vendor ? <><br />“Getting from {conflict.vendor}”</> : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
            <Button onClick={() => { setError(null); setStep('join') }}>
              Join {conflict.displayName}’s order
            </Button>
            <Button variant="secondary" onClick={() => { setConflict(null); setCategory(null); setStep('category') }}>
              Order something else
            </Button>
            <Button variant="tertiary" onClick={() => setStep('mode')}>
              Order {lowerLabel(conflict.category)} anyway
            </Button>
          </div>
        </>
      ) : null}

      {step === 'join' && conflict ? (
        <>
          <div id="sfg-food-add" style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
            Join {conflict.displayName}’s order
          </div>
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
            They place it and they pay. This is a request, not an edit. It won’t change the
            “enough for” estimate.
          </div>
          <Spacer h={18} />
          <Field
            label="What do you want?"
            inSheet
            autoFocus
            maxLength={140}
            placeholder="one more veggie"
            value={request}
            onChange={(e) => { setRequest(e.currentTarget.value); setError(null) }}
          />
          {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}
          <Spacer h={18} />
          <Button disabled={busy} onClick={() => void postJoin()}>
            {busy ? 'Sending…' : `Ask ${conflict.displayName}`}
          </Button>
          <Spacer h={8} />
          <Button variant="tertiary" onClick={() => setStep('conflict')}>Back</Button>
        </>
      ) : null}

      {step === 'mode' && category ? (
        <>
          <div id="sfg-food-add" style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
            {emojiFor(category)} {labelFor(category)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
            Posting before you order is what stops two people buying the same thing.
          </div>
          <Spacer h={18} />
          <Field
            label="Roughly how much? (optional)"
            inSheet
            maxLength={40}
            placeholder="50, or 3 large"
            value={approxQty}
            onChange={(e) => setApproxQty(e.currentTarget.value)}
          />
          {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
            <Button disabled={busy} onClick={() => void postIntent()}>
              {busy ? 'Posting…' : 'I’m about to order'}
            </Button>
            <Button variant="secondary" onClick={() => { setError(null); setStep('details') }}>
              I already placed it
            </Button>
          </div>
        </>
      ) : null}

      {step === 'details' && category ? (
        <>
          <div id="sfg-food-add" style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
            {labelFor(category)} details
          </div>

          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ position: 'relative' }}>
              <Field
                label="From"
                inSheet
                maxLength={80}
                placeholder="The pizza place"
                value={vendor}
                onChange={(e) => { setVendor(e.currentTarget.value); setVendorOpen(true) }}
                onFocus={() => setVendorOpen(true)}
                role="combobox"
                aria-expanded={vendorOpen && vendorMatches.length > 0}
                aria-autocomplete="list"
              />
              {vendorOpen && vendorMatches.length ? (
                <div
                  role="listbox"
                  aria-label="Places you’ve ordered from"
                  style={{
                    marginTop: 6, borderRadius: 12, border: '1px solid var(--line)',
                    background: 'var(--bg)', overflow: 'hidden',
                  }}
                >
                  {vendorMatches.map((v) => (
                    <button
                      key={v.vendorKey}
                      role="option"
                      aria-selected={v.vendor === vendor}
                      onClick={() => {
                        setVendor(v.vendor)
                        setVendorOpen(false)
                        const typical = v.typicalEtaMin
                        if (typical !== null) {
                          // Snap the ETA chips to whatever this place usually takes.
                          const nearest = ETA_CHOICES.reduce<number>(
                            (best, n) => (Math.abs(n - typical) < Math.abs(best - typical) ? n : best),
                            ETA_CHOICES[0],
                          )
                          setEtaMinutes(nearest)
                        }
                      }}
                      style={{
                        display: 'flex', width: '100%', justifyContent: 'space-between',
                        padding: '12px 14px', minHeight: 44, fontSize: 14, textAlign: 'left',
                        borderBottom: '1px solid var(--line)',
                      }}
                    >
                      <span>{v.vendor}</span>
                      <span className="mono" style={{ fontSize: 11, color: 'var(--dim)' }}>
                        {v.typicalEtaMin ? `~${v.typicalEtaMin} min` : `${v.uses}×`}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <Field
              label="What"
              inSheet
              maxLength={200}
              placeholder="6 large, 2 veggie"
              value={contents}
              onChange={(e) => { setContents(e.currentTarget.value); setError(null) }}
            />

            <div>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>Arriving in</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {ETA_CHOICES.map((n) => {
                  const on = etaMinutes === n
                  return (
                    <button
                      key={n}
                      aria-pressed={on}
                      onClick={() => setEtaMinutes(n)}
                      style={{
                        flex: 1, textAlign: 'center', padding: 11, minHeight: 44, borderRadius: 10,
                        fontSize: 13, fontWeight: on ? 700 : 400,
                        background: on ? 'var(--fill)' : 'transparent',
                        color: on ? 'var(--ink)' : 'var(--fg)',
                        border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
                      }}
                    >
                      {n}
                    </button>
                  )
                })}
              </div>
            </div>

            <Field
              label="Notes"
              inSheet
              maxLength={140}
              placeholder="optional, 140 characters"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </div>

          {vendorHint ? (
            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 14 }}>{vendorHint}</div>
          ) : null}
          {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}

          <Spacer h={18} />
          <Button disabled={busy} onClick={() => void postOrder()}>
            {busy ? 'Posting…' : 'Post it'}
          </Button>
          <Spacer h={8} />
          <Button variant="tertiary" onClick={() => setStep('mode')}>Back</Button>
        </>
      ) : null}
    </Sheet>
  )
}

// ── edit (owner or admin, D56) ──────────────────────────────────────────────

function EditSheet({
  order, onClose, onDone,
}: {
  order: FoodOrder | null
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const [contents, setContents] = useState('')
  const [notes, setNotes] = useState('')
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seeded = useRef<string | null>(null)

  useEffect(() => {
    if (!order || seeded.current === order.id) return
    seeded.current = order.id
    setContents(order.contents)
    setNotes(order.notes ?? '')
    setEtaMinutes(null)
    setError(null)
  }, [order])

  useEffect(() => { if (!order) seeded.current = null }, [order])

  if (!order) return null

  const save = async () => {
    const what = contents.trim()
    if (!what) { setError('An order needs a description.'); return }
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = { contents: what, notes: notes.trim() || null }
      if (etaMinutes !== null) body.etaMinutes = etaMinutes
      const res = await writeOrQueue(`/api/food/orders/${order.id}`, 'PATCH', body, 'Edited an order')
      if (res === null) setError(null)
      await onDone()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t save that.')
    } finally {
      setBusy(false)
    }
  }

  const cancelOrder = async () => {
    setBusy(true)
    try {
      await DEL(`/api/food/orders/${order.id}`)
      await onDone()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t cancel that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open onClose={onClose} labelledBy="sfg-food-edit">
      <Eyebrow large>Edit · everyone sees changes</Eyebrow>
      <div id="sfg-food-edit" style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
        {emojiFor(order.category)} {order.vendor ?? labelFor(order.category)}
      </div>

      <Spacer h={18} />
      <Field
        label="What"
        inSheet
        maxLength={200}
        value={contents}
        onChange={(e) => { setContents(e.currentTarget.value); setError(null) }}
      />
      <Spacer h={14} />
      <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>New ETA, from now</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {ETA_CHOICES.map((n) => {
          const on = etaMinutes === n
          return (
            <button
              key={n}
              aria-pressed={on}
              onClick={() => setEtaMinutes(on ? null : n)}
              style={{
                flex: 1, textAlign: 'center', padding: 11, minHeight: 44, borderRadius: 10,
                fontSize: 13, fontWeight: on ? 700 : 400,
                background: on ? 'var(--fill)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--fg)',
                border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
              }}
            >
              {n}
            </button>
          )
        })}
      </div>
      <Spacer h={14} />
      <Field
        label="Notes"
        inSheet
        maxLength={140}
        placeholder="optional, 140 characters"
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />

      {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}

      <Spacer h={18} />
      <Button disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
      <Spacer h={8} />
      <Button variant="tertiary" disabled={busy} onClick={() => void cancelOrder()}>
        Cancel this order
      </Button>
    </Sheet>
  )
}

// ── join an existing order or intent (D117) ─────────────────────────────────

function JoinSheet({
  target, orders, onClose, onDone,
}: {
  target: { order: FoodOrder | null; intent: FoodIntent | null } | null
  orders: FoodOrder[]
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const [request, setRequest] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setRequest(''); setError(null) }, [target])

  if (!target) return null

  const intent = target.intent
  // An intent has no order to hang an add-on from until it converts (D117) —
  // if it already has, that order is the thing to join.
  const order = target.order
    ?? (intent
      ? orders.find((o) => o.userId === intent.userId && o.category === intent.category
        && !o.deliveredAt && !o.cancelledAt) ?? null
      : null)
  const who = order?.displayName ?? intent?.displayName ?? 'them'

  const send = async () => {
    const text = request.trim()
    if (!text) { setError('Say what you want added.'); return }
    if (!order) {
      setError(`${who} hasn’t placed it yet. You can add to it the moment they do.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      await writeOrQueue(`/api/food/orders/${order.id}/join`, 'POST', { request: text }, `Added to ${who}’s order`)
      await onDone()
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t add that request.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open onClose={onClose} labelledBy="sfg-food-join">
      <Eyebrow large>Add to an order</Eyebrow>
      <div id="sfg-food-join" style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
        Join {who}’s order
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>
        {order
          ? `${order.vendor ?? labelFor(order.category)} · ${order.contents}`
          : `${who} hasn’t placed it yet. Type what you want. You can send it the moment they do.`}
      </div>

      <Spacer h={18} />
      <Field
        label="What do you want?"
        inSheet
        autoFocus
        maxLength={140}
        placeholder="one more veggie"
        value={request}
        onChange={(e) => { setRequest(e.currentTarget.value); setError(null) }}
      />
      {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}

      <Spacer h={18} />
      <Button disabled={busy} onClick={() => void send()}>
        {busy ? 'Sending…' : `Ask ${who}`}
      </Button>
      <Spacer h={8} />
      <Button variant="tertiary" onClick={onClose}>Never mind</Button>
    </Sheet>
  )
}
